'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

import { modalManager } from '@/components/Modal/lib/modalManager';
import { ModalName } from '@/constants';
import { useHash } from '@/hooks/useHash';

const HashModal = dynamic(() =>
    import('@/components/Modal/HashModal/HashModal').then((module) => module.HashModal),
);

const MODAL_HASHES = Object.values(ModalName);

export function HashModalManager() {
    const hash = useHash();
    const [visibleHashModal, setVisibleHashModal] = useState<ModalName | null>(null);
    const [hashModalParameters, setHashModalParameters] = useState<string | undefined>();

    useEffect(() => {
        modalManager.initialize();
    }, []);

    // Handle modals that use hash logic
    useEffect(() => {
        if (!hash) {
            setVisibleHashModal(null);
            setHashModalParameters(undefined);
            return;
        }

        // Parameters can be a string with dashes.
        // Examples:
        // - Wistia videos hash example: `video-jvr5k0rwt9`
        // - Embed video block deep-linked hash example: `video-fd173d53-0921-48b9-a81c-fe593453fea7
        const [modalName, ...parameters] = hash.split('-');
        const isModalHash = MODAL_HASHES.includes(modalName as ModalName);

        // A hash can be used to deep-link to an element on the page.
        // Even if it starts with a known Modal hash prefix, we don't want to open a modal in this case.
        // @see [CARE-2949]
        const isDeepLink =
            isLookingLikeUuid(parameters.join('-')) ||
            Boolean(document.getElementById(hash) ?? document.querySelector(`a[name="${hash}"]`));

        if (isDeepLink) {
            setVisibleHashModal(null);
            setHashModalParameters(undefined);
            return;
        }

        if (isModalHash) {
            setVisibleHashModal(modalName as ModalName);
            setHashModalParameters(parameters.join('-'));
        }
    }, [hash]);

    if (visibleHashModal) {
        return <HashModal modalHash={visibleHashModal} parameters={hashModalParameters} />;
    }
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isLookingLikeUuid(value: string): boolean {
    return Boolean(value.match(UUID_REGEX));
}
